import config from '@/config.js'
import { getStatic } from '@/services/data-source.js'

const WORD_FORM_SEARCH_SCHEMA_VERSION = 1
const WORD_FORM_SEARCH_ENTITIES = Object.freeze(['internal', 'external', 'lemma'])
const WORD_FORM_SEARCH_ENTITY_SET = new Set(WORD_FORM_SEARCH_ENTITIES)

let compactIndexPromise = null
let compactIndexBaseUrl = null
let compactMissingBaseUrl = null
const compactShardPromises = new Map()

function isObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function unaccentWordForm(value) {
  return String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '')
}

function searchableWordForm(value) {
  return unaccentWordForm(String(value || '').replace(/<[^>]*>/g, ''))
}

function compileWordFormPattern(pattern) {
  const expression = String(pattern || '').trim()
  if (!expression) return null
  try {
    return new RegExp(unaccentWordForm(expression), 'iu')
  } catch (error) {
    throw new Error(`Invalid search regular expression: ${error.message}`)
  }
}

function selectedEntities(filters) {
  const values = filters === undefined
    ? WORD_FORM_SEARCH_ENTITIES
    : filters
  const entities = new Set((values || []).map(String))
  const unsupported = [...entities].find(entity => !WORD_FORM_SEARCH_ENTITY_SET.has(entity))
  if (unsupported) {
    throw new Error(`Unsupported word-form search entity: ${unsupported}`)
  }
  return entities
}

function collectMatchingLemmaIds(rows, expression, formAt, lemmaIdsAt) {
  const lemmaIds = new Set()
  rows.forEach(row => {
    if (!expression.test(searchableWordForm(formAt(row)))) return
    lemmaIdsAt(row).forEach(lemmaId => lemmaIds.add(String(lemmaId)))
  })
  return lemmaIds
}

function normalizeCompactIndex(payload) {
  if (
    !isObject(payload) ||
    payload.schemaVersion !== WORD_FORM_SEARCH_SCHEMA_VERSION ||
    !Array.isArray(payload.lemmaIds) ||
    !isObject(payload.entities)
  ) {
    throw new Error('Word-form search index is malformed')
  }
  if (WORD_FORM_SEARCH_ENTITIES.some(entity => !Number.isInteger(payload.entities[entity]))) {
    throw new Error('Word-form search index entity counts are malformed')
  }
  return Object.freeze({
    schemaVersion: payload.schemaVersion,
    lemmaIds: Object.freeze(payload.lemmaIds.map(String)),
    entities: Object.freeze({ ...payload.entities }),
  })
}

function normalizeCompactShard(payload, entity, index) {
  if (
    !isObject(payload) ||
    payload.schemaVersion !== WORD_FORM_SEARCH_SCHEMA_VERSION ||
    payload.entity !== entity ||
    !Array.isArray(payload.rows)
  ) {
    throw new Error(`Word-form search ${entity} shard is malformed`)
  }
  payload.rows.forEach((row, rowIndex) => {
    if (
      !Array.isArray(row) ||
      row.length !== 2 ||
      typeof row[0] !== 'string' ||
      !Array.isArray(row[1]) ||
      row[1].some(idIndex => !Number.isInteger(idIndex) || idIndex < 0 || idIndex >= index.lemmaIds.length)
    ) {
      throw new Error(`Word-form search ${entity} row ${rowIndex} is malformed`)
    }
  })
  return Object.freeze(payload.rows)
}

function loadCompactIndex() {
  const baseUrl = String(config.apiUrlClientSide || '')
  if (!compactIndexPromise || compactIndexBaseUrl !== baseUrl) {
    compactIndexBaseUrl = baseUrl
    compactIndexPromise = getStatic('word_form_search/index.json')
      .then(response => normalizeCompactIndex(response.data))
      .catch(error => {
        compactIndexPromise = null
        compactIndexBaseUrl = null
        if (error.response && error.response.status === 404) {
          error.code = 'STATIC_WORD_FORM_INDEX_NOT_FOUND'
        }
        throw error
      })
  }
  return compactIndexPromise
}

async function loadCompactShard(entity, index) {
  const baseUrl = String(config.apiUrlClientSide || '')
  const key = `${baseUrl}\u0000${entity}`
  if (!compactShardPromises.has(key)) {
    compactShardPromises.set(
      key,
      getStatic(`word_form_search/${entity}.json`)
        .then(response => normalizeCompactShard(response.data, entity, index))
        .catch(error => {
          compactShardPromises.delete(key)
          throw error
        })
    )
  }
  return compactShardPromises.get(key)
}

async function queryCompactWordForms(criteria) {
  const expression = compileWordFormPattern(criteria.pattern)
  const entities = selectedEntities(criteria.filters)
  if (!expression || !entities.size) {
    return { lemmaIds: new Set(), legacyFallback: false, compact: true }
  }

  const index = await loadCompactIndex()
  const shards = await Promise.all(
    [...entities].sort().map(async entity => ({
      entity,
      rows: await loadCompactShard(entity, index),
    }))
  )
  const lemmaIds = new Set()
  shards.forEach(shard => {
    collectMatchingLemmaIds(
      shard.rows,
      expression,
      row => row[0],
      row => row[1].map(idIndex => index.lemmaIds[idIndex])
    ).forEach(lemmaId => lemmaIds.add(lemmaId))
  })
  return { lemmaIds, legacyFallback: false, compact: true }
}

async function searchAllWordForms(criteria, legacySearch) {
  const expression = compileWordFormPattern(criteria.pattern)
  const entities = selectedEntities(criteria.filters)
  if (!expression || !entities.size) {
    return { lemmaIds: new Set(), legacyFallback: false, compact: false }
  }

  const baseUrl = String(config.apiUrlClientSide || '')
  if (compactMissingBaseUrl !== baseUrl) {
    try {
      return await queryCompactWordForms(criteria)
    } catch (error) {
      if (error.code !== 'STATIC_WORD_FORM_INDEX_NOT_FOUND') throw error
      compactMissingBaseUrl = baseUrl
    }
  }

  const response = await legacySearch(criteria)
  return {
    lemmaIds: new Set(response.data.map(row => String(row.lemmaId))),
    legacyFallback: response.legacyFallback,
    compact: false,
  }
}

function clearStaticWordFormSearchCache() {
  compactIndexPromise = null
  compactIndexBaseUrl = null
  compactMissingBaseUrl = null
  compactShardPromises.clear()
}

export {
  WORD_FORM_SEARCH_ENTITIES,
  clearStaticWordFormSearchCache,
  collectMatchingLemmaIds,
  compileWordFormPattern,
  queryCompactWordForms,
  searchAllWordForms,
  searchableWordForm,
  unaccentWordForm,
}
